import React, {useEffect, useRef, useState} from 'react';
import {useSpecialistApi} from '../../core/hooks/use-specialist-api';
import {ReactComponent as ImageIcon} from "@assets/icons/image.svg";
import Base64Helper from "@utils/base64-helper";

const specialistImageCache = new Map<string, string | null>();

interface SpecialistProfileImageProps {
    specialistId: string;
    specialistName: string;
    className?: string;
    classNameImg?: string;
    forceLoad?: boolean;
}

const SpecialistProfileImage: React.FC<SpecialistProfileImageProps> = (props) => {
    const {
        specialistId,
        specialistName,
        className = '',
        classNameImg = "",
        forceLoad = false,
    } = props;
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const {getSpecialistProfileImage} = useSpecialistApi();
    const imgRef = useRef<HTMLDivElement>(null);

    const getInitials = (name: string) =>
        name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('');

    const loadImage = async (forceReload: boolean = false) => {
        if (isLoading) return;

        if (!forceReload) {
            if (imageUrl || hasError) return;

            if (specialistImageCache.has(specialistId)) {
                const cached = specialistImageCache.get(specialistId) ?? null;
                if (cached) {
                    setImageUrl(cached);
                    setHasError(false);
                } else {
                    setImageUrl(null);
                    setHasError(true);
                }
                return;
            }
        }

        setIsLoading(true);
        setHasError(false);

        try {
            const response = await getSpecialistProfileImage(specialistId);
            const url = Base64Helper.addBase64Prefix(response?.base64);
            specialistImageCache.set(specialistId, url || null);
            setImageUrl(url);
        } catch (error) {
            console.error('Error loading specialist image:', error);
            specialistImageCache.set(specialistId, null);
            setHasError(true);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (forceLoad) {
            void loadImage();
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    void loadImage();
                    observer.disconnect();
                }
            },
            {threshold: 0.1}
        );

        if (imgRef.current) {
            observer.observe(imgRef.current);
        }

        return () => observer.disconnect();
    }, [specialistId, forceLoad]);

    useEffect(() => {
        const handleSpecialistsRefresh = () => {
            specialistImageCache.delete(specialistId);
            setImageUrl(null);
            setHasError(false);
            void loadImage(true);
        };

        const handleSpecialistImageUpdate = (event: CustomEvent) => {
            const {specialistId: updatedSpecialistId} = event.detail;
            if (updatedSpecialistId === specialistId) {
                specialistImageCache.delete(specialistId);
                setImageUrl(null);
                setHasError(false);
                void loadImage(true);
            }
        };

        window.addEventListener('specialists:refresh', handleSpecialistsRefresh);
        window.addEventListener('specialist:image-updated', handleSpecialistImageUpdate as EventListener);

        return () => {
            window.removeEventListener('specialists:refresh', handleSpecialistsRefresh);
            window.removeEventListener('specialist:image-updated', handleSpecialistImageUpdate as EventListener);
        };
    }, [specialistId]);

    return (
        <div
            ref={imgRef}
            className={`rounded-lg flex items-center justify-center ${className} ${
                isLoading ? 'animate-pulse' : ''
            }`}
            style={{
                minWidth: '24px',
                minHeight: '24px',
                width: className.includes('w-') ? undefined : '32px',
                height: className.includes('h-') ? undefined : '32px'
            }}
        >
            {imageUrl && !isLoading && !hasError ? (
                <img
                    src={imageUrl}
                    alt={`Zdjęcie ${specialistName}`}
                    className={"w-full h-full object-cover rounded-lg " + classNameImg}
                    onError={() => setHasError(true)}
                />
            ) : isLoading ? (
                <div className={"w-full h-full bg-gray-200 rounded-lg flex items-center justify-center animate-pulse " + classNameImg}>
                    <ImageIcon className="w-4 h-4 text-gray-400"/>
                </div>
            ) : (
                <div className={"w-full h-full bg-gray-200 rounded-lg flex items-center justify-center " + classNameImg}>
                    <span className="text-gray-600 font-semibold text-sm">{getInitials(specialistName)}</span>
                </div>
            )}
        </div>
    );
};

export default SpecialistProfileImage;
